import { useEffect, useMemo, useState } from "react";

import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

import { AuthClient } from "@nav-games/auth-client";

import { AUTH_CLIENT_OPTIONS } from "./config";
import { refreshEntitlements } from "./entitlements";
import { AuthCallback } from "./pages/AuthCallback";
import { Home } from "./pages/Home";
import { Me } from "./pages/Me";
import { Play } from "./pages/Play";
import { Drills } from "./pages/Drills";
import { Morse } from "./pages/morse/Morse";
import { Flashcards } from "./pages/morse/Flashcards";
import { Hangman } from "./pages/morse/Hangman";
import { HangmanMulti } from "./pages/morse/HangmanMulti";
import { Translate as MorseTranslate } from "./pages/morse/Translate";
import { Semaphore } from "./pages/semaphore/Semaphore";
import { Flashcards as SemaphoreFlashcards } from "./pages/semaphore/Flashcards";
import { Numerals as SemaphoreNumerals } from "./pages/semaphore/Numerals";
import { Translate as SemaphoreTranslate } from "./pages/semaphore/Translate";
import { Flags } from "./pages/flags/Flags";
import { Flashcards as FlagsFlashcards } from "./pages/flags/Flashcards";
import { Meanings as FlagsMeanings } from "./pages/flags/Meanings";
import { Reverse as FlagsReverse } from "./pages/flags/Reverse";
import { Combos as FlagsCombos } from "./pages/flags/Combos";
import { CombosReverse as FlagsCombosReverse } from "./pages/flags/CombosReverse";
import { Translate as FlagsTranslate } from "./pages/flags/Translate";
import { Distress } from "./pages/distress/Distress";
import { Flashcards as DistressFlashcards } from "./pages/distress/Flashcards";
import { Mayday } from "./pages/mayday/Mayday";
import { Builder as MaydayBuilder } from "./pages/mayday/Builder";
import { Nato as MaydayNato } from "./pages/mayday/Nato";
import { NatoReview as MaydayNatoReview } from "./pages/mayday/NatoReview";
import { Vhf as MaydayVhf } from "./pages/mayday/Vhf";
import { Chartwork } from "./pages/chartwork/Chartwork";
import { Flashcards as ChartworkFlashcards } from "./pages/chartwork/Flashcards";
import { CompassConvert } from "./pages/chartwork/CompassConvert";
import { CompassSigns } from "./pages/chartwork/CompassSigns";
import { Cts as ChartworkCts } from "./pages/chartwork/Cts";
import { DeviationLookup } from "./pages/chartwork/DeviationLookup";
import { Ep as ChartworkEp } from "./pages/chartwork/Ep";
import { Dst as ChartworkDst } from "./pages/chartwork/Dst";
import { Astro } from "./pages/astro/Astro";
import { Corrections as AstroCorrections } from "./pages/astro/Corrections";
import { SightReduction as AstroSightReduction } from "./pages/astro/SightReduction";
import { Reading as AstroReading } from "./pages/astro/Reading";
import { StarId as AstroStarId } from "./pages/astro/StarId";
import { SpotError as AstroSpotError } from "./pages/astro/SpotError";
import { AlmanacDrill as AstroAlmanacDrill } from "./pages/astro/AlmanacDrill";
import { AbcDrill as AstroAbcDrill } from "./pages/astro/AbcDrill";
import { ProForma as AstroProForma } from "./pages/astro/ProForma";
import { Sailing } from "./pages/sailing/Sailing";
import { Parts as SailingParts } from "./pages/sailing/Parts";
import { Points as SailingPoints } from "./pages/sailing/Points";
import { Sequences as SailingSequences } from "./pages/sailing/Sequences";
import { BoatParts as SailingBoatParts } from "./pages/sailing/BoatParts";
import { Wobble as PreDepartureWobble } from "./pages/predeparture/Wobble";
import { MnemonicsDrill } from "./pages/mnemonics/Drill";
import { Safety } from "./pages/safety/Safety";
import { EngineDrill } from "./pages/engine/EngineDrill";
import { Knots } from "./pages/knots/Knots";
import { Anchoring } from "./pages/anchoring/Anchoring";
import { AnchorFlashcards } from "./pages/anchoring/Flashcards";
import { Techniques as AnchoringTechniques } from "./pages/anchoring/Techniques";
import { Gps } from "./pages/gps/Gps";
import { Mob } from "./pages/mob/Mob";
import { Pilotage } from "./pages/pilotage/Pilotage";
import { ThreePointFix as PilotageThreePointFix } from "./pages/pilotage/ThreePointFix";
import { PreDeparture } from "./pages/predeparture/PreDeparture";
import { Tides } from "./pages/tides/Tides";
import { TidalLevelsGame } from "./pages/tides/Levels";
import { Twelfths as TidesTwelfths } from "./pages/tides/Twelfths";
import { Soundings } from "./pages/soundings/Soundings";
import { SoundingsMulti } from "./pages/soundings/SoundingsMulti";
import { DayShapes } from "./pages/day-shapes/DayShapes";
import { Flashcards as DayShapesFlashcards } from "./pages/day-shapes/Flashcards";
import { Buoyage } from "./pages/buoyage/Buoyage";
import { Flashcards as BuoyageFlashcards } from "./pages/buoyage/Flashcards";
import { Irpcs } from "./pages/irpcs/Irpcs";
import { Scenarios as IrpcsScenarios } from "./pages/irpcs/Scenarios";
import { Lights } from "./pages/lights/Lights";
import { Flashcards as LightsFlashcards } from "./pages/lights/Flashcards";
import { Weather } from "./pages/weather/Weather";
import { Flashcards as WeatherFlashcards } from "./pages/weather/Flashcards";
import { Synoptic as WeatherSynoptic } from "./pages/weather/Synoptic";
import { Reference } from "./pages/Reference";
import { BroadcastListen } from "./pages/BroadcastListen";
import { Exam } from "./pages/exam/Exam";
import { Multiplayer } from "./pages/Multiplayer";
import { Logo } from "./ui/Logo";

/// Routes:
///   /                 — Home: marketing splash + sign-in (public).
///                       Auto-redirects to /play when already signed in.
///   /auth/callback    — consume the URL fragment, save tokens, → /play
///   /me               — account + linked-identities (private).
///   /play             — hub: list of modules (private).
///   /play/morse       — Morse module: demos + games (private).
///   /play/morse/<game> — individual games (private). Built one at a time.
///
/// Tiny custom router — no need to take a dependency on react-router
/// at this size. `popstate` covers back/forward; `navigate()` covers
/// link clicks and post-action redirects.
///
/// Private-route gating: every path under /me, /play is gated on
/// `auth.session()` being truthy. When the gate fails we render the
/// sign-in card directly so the user doesn't see a flash of empty
/// content before a redirect — and we set the URL to `/` so the
/// back button doesn't loop them straight back through the gate.

const PUBLIC_PATHS = new Set(["/", "/auth/callback"]);

function isPrivate(pathname: string): boolean {
  return !PUBLIC_PATHS.has(pathname);
}

export function App() {
  const auth = useMemo(() => new AuthClient(AUTH_CLIENT_OPTIONS), []);
  const [pathname, setPathname] = useState(window.location.pathname);
  // Track session presence reactively so sign-in / sign-out in any
  // child component re-renders the gate. The AuthClient doesn't emit
  // events today; instead the children call onNavigate after
  // auth.signOut() / consume tokens, which already triggers a route
  // change. We snapshot it once per render.
  //
  // `consumedCallback` is bumped after we consume an inbound
  // sign-in fragment so the session lookup below re-reads the
  // freshly persisted tokens before the gate runs.
  const [consumedCallback, setConsumedCallback] = useState(0);
  // Reads the most recent persisted session on every render. The
  // counter triggers re-evaluation after a successful callback.
  void consumedCallback;
  const session = auth.session();

  // Sign-in handoff — goastern-auth redirects back here with the
  // session in `#access=…&refresh=…&expires=…&audience=…`. Web
  // delivers it as `window.location.hash` (the worker redirects the
  // SPA to its returnUrl). Capacitor delivers it via the custom
  // `goastern-nav://auth/complete` URI scheme, surfacing through
  // `@capacitor/app`'s `appUrlOpen` event — the WebView never
  // navigates, the intent system hands the URL straight to the app.
  useEffect(() => {
    // Web path: fragment is already in window.location.
    if (window.location.hash.includes("access=")) {
      const session = auth.consumeCallback();
      window.history.replaceState({}, "", "/play");
      setPathname("/play");
      if (session) setConsumedCallback((n) => n + 1);
    }

    // Capacitor path: subscribe to the custom-scheme intent.
    if (!Capacitor.isNativePlatform()) return;
    let handle: { remove: () => Promise<void> } | null = null;
    void CapacitorApp.addListener("appUrlOpen", (event: { url: string }) => {
      if (!event.url.includes("access=")) return;
      // OAuth via Apple/Google completes inside an in-app
      // SFSafariViewController; dismiss it now that the custom-scheme
      // redirect has handed the session back to the app.
      void Browser.close();
      // `consumeCallback` accepts a URL; pass the intent URL so it
      // parses the fragment from there rather than window.location.
      const url = new URL(event.url);
      const session = auth.consumeCallback(url);
      window.history.replaceState({}, "", "/play");
      setPathname("/play");
      if (session) setConsumedCallback((n) => n + 1);
    }).then((h) => {
      handle = h;
    });
    return () => {
      void handle?.remove();
    };
  }, [auth]);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Sync the local `isPremium()` flag against the worker's
  // authoritative entitlement state on every fresh sign-in. The
  // worker pushes renewal / revocation updates via its App Store +
  // Play webhooks, so the SPA's role is just to read once after
  // auth and once after a successful IAP purchase (handled
  // alongside the StoreKit / BillingClient callback, future).
  useEffect(() => {
    if (!session) return;
    void refreshEntitlements(auth);
    // `consumedCallback` re-runs this after a token lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken, consumedCallback]);

  const navigate = (to: string) => {
    window.history.pushState({}, "", to);
    setPathname(to);
  };

  // Gate: if you ask for a private path without a session, fall back
  // to the public landing page. Replace the URL so the back button
  // doesn't bounce you back through the gate.
  if (isPrivate(pathname) && !session) {
    window.history.replaceState({}, "", "/");
    return (
      <Shell pathname="/" onNavigate={navigate}>
        <Home auth={auth} onNavigate={navigate} />
      </Shell>
    );
  }

  let page;
  if (pathname === "/auth/callback") {
    page = <AuthCallback auth={auth} onDone={(to) => navigate(to)} />;
  } else if (pathname === "/me") {
    page = <Me auth={auth} onSignOut={() => navigate("/")} />;
  } else if (pathname === "/play") {
    page = <Play auth={auth} onNavigate={navigate} />;
  } else if (pathname === "/play/morse") {
    page = <Morse onNavigate={navigate} />;
  } else if (pathname === "/play/morse/flashcards") {
    page = <Flashcards auth={auth} onNavigate={navigate} />;
  } else if (pathname === "/play/morse/hangman") {
    page = <Hangman onNavigate={navigate} />;
  } else if (pathname === "/play/morse/hangman-multi") {
    page = <HangmanMulti onNavigate={navigate} />;
  } else if (pathname === "/play/morse/translate") {
    page = <MorseTranslate onNavigate={navigate} />;
  } else if (pathname === "/play/semaphore") {
    page = <Semaphore onNavigate={navigate} />;
  } else if (pathname === "/play/semaphore/flashcards") {
    page = <SemaphoreFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/semaphore/numerals") {
    page = <SemaphoreNumerals onNavigate={navigate} />;
  } else if (pathname === "/play/semaphore/translate") {
    page = <SemaphoreTranslate onNavigate={navigate} />;
  } else if (pathname === "/play/flags") {
    page = <Flags onNavigate={navigate} />;
  } else if (pathname === "/play/flags/flashcards") {
    page = <FlagsFlashcards auth={auth} onNavigate={navigate} />;
  } else if (pathname === "/play/flags/meanings") {
    page = <FlagsMeanings onNavigate={navigate} />;
  } else if (pathname === "/play/flags/reverse") {
    page = <FlagsReverse onNavigate={navigate} />;
  } else if (pathname === "/play/flags/combos") {
    page = <FlagsCombos onNavigate={navigate} />;
  } else if (pathname === "/play/flags/combos-reverse") {
    page = <FlagsCombosReverse onNavigate={navigate} />;
  } else if (pathname === "/play/flags/translate") {
    page = <FlagsTranslate onNavigate={navigate} />;
  } else if (pathname === "/play/lights") {
    page = <Lights onNavigate={navigate} />;
  } else if (pathname === "/play/lights/flashcards") {
    page = <LightsFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/weather") {
    page = <Weather onNavigate={navigate} />;
  } else if (pathname === "/play/weather/flashcards") {
    page = <WeatherFlashcards direction="forward" onNavigate={navigate} />;
  } else if (pathname === "/play/weather/reverse") {
    page = <WeatherFlashcards direction="reverse" onNavigate={navigate} />;
  } else if (pathname === "/play/weather/synoptic") {
    page = <WeatherSynoptic onNavigate={navigate} />;
  } else if (pathname === "/play/distress") {
    page = <Distress onNavigate={navigate} />;
  } else if (pathname === "/play/distress/flashcards") {
    page = <DistressFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/mayday") {
    page = <Mayday onNavigate={navigate} />;
  } else if (pathname === "/play/mayday/builder") {
    page = <MaydayBuilder onNavigate={navigate} />;
  } else if (pathname === "/play/mayday/nato") {
    page = <MaydayNato onNavigate={navigate} />;
  } else if (pathname === "/play/mayday/nato-review") {
    page = <MaydayNatoReview onNavigate={navigate} />;
  } else if (pathname === "/play/mayday/vhf") {
    page = <MaydayVhf onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork") {
    page = <Chartwork onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/flashcards") {
    page = <ChartworkFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/compass") {
    page = <CompassConvert onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/signs") {
    page = <CompassSigns onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/cts") {
    page = <ChartworkCts onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/deviation") {
    page = <DeviationLookup onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/ep") {
    page = <ChartworkEp onNavigate={navigate} />;
  } else if (pathname === "/play/chartwork/dst") {
    page = <ChartworkDst onNavigate={navigate} />;
  } else if (pathname === "/play/astro") {
    page = <Astro onNavigate={navigate} />;
  } else if (pathname === "/play/astro/corrections") {
    page = <AstroCorrections onNavigate={navigate} />;
  } else if (pathname === "/play/astro/sight-reduction") {
    page = <AstroSightReduction onNavigate={navigate} />;
  } else if (pathname === "/play/astro/reading") {
    page = <AstroReading onNavigate={navigate} />;
  } else if (pathname === "/play/astro/star-id") {
    page = <AstroStarId onNavigate={navigate} />;
  } else if (pathname === "/play/astro/spot-error") {
    page = <AstroSpotError onNavigate={navigate} />;
  } else if (pathname === "/play/astro/almanac") {
    page = <AstroAlmanacDrill onNavigate={navigate} />;
  } else if (pathname === "/play/astro/abc") {
    page = <AstroAbcDrill onNavigate={navigate} />;
  } else if (pathname === "/play/astro/pro-forma") {
    page = <AstroProForma onNavigate={navigate} />;
  } else if (pathname === "/play/sailing") {
    page = <Sailing onNavigate={navigate} />;
  } else if (pathname === "/play/sailing/parts") {
    page = <SailingParts onNavigate={navigate} />;
  } else if (pathname === "/play/sailing/points") {
    page = <SailingPoints onNavigate={navigate} />;
  } else if (pathname === "/play/sailing/sequences") {
    page = <SailingSequences onNavigate={navigate} />;
  } else if (pathname === "/play/sailing/boat-parts") {
    page = <SailingBoatParts onNavigate={navigate} />;
  } else if (pathname === "/drills/predeparture/wobble") {
    page = <PreDepartureWobble onNavigate={navigate} />;
  } else if (pathname === "/play/mnemonics") {
    page = <MnemonicsDrill onNavigate={navigate} />;
  } else if (pathname === "/play/safety") {
    page = <Safety onNavigate={navigate} />;
  } else if (pathname === "/play/engine") {
    page = <EngineDrill onNavigate={navigate} />;
  } else if (pathname === "/play/knots") {
    page = <Knots onNavigate={navigate} />;
  } else if (pathname === "/play/anchoring") {
    page = <Anchoring onNavigate={navigate} />;
  } else if (pathname === "/play/anchoring/flashcards") {
    page = <AnchorFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/anchoring/techniques") {
    page = <AnchoringTechniques onNavigate={navigate} />;
  } else if (pathname === "/play/gps") {
    page = <Gps onNavigate={navigate} />;
  } else if (pathname === "/drills") {
    page = <Drills onNavigate={navigate} />;
  } else if (pathname === "/drills/mob") {
    page = <Mob onNavigate={navigate} />;
  } else if (pathname === "/drills/predeparture") {
    page = <PreDeparture onNavigate={navigate} />;
  } else if (pathname === "/play/tides") {
    page = <Tides onNavigate={navigate} />;
  } else if (pathname === "/play/tides/levels") {
    page = <TidalLevelsGame onNavigate={navigate} />;
  } else if (pathname === "/play/tides/twelfths") {
    page = <TidesTwelfths onNavigate={navigate} />;
  } else if (pathname === "/play/pilotage") {
    page = <Pilotage onNavigate={navigate} />;
  } else if (pathname === "/play/pilotage/three-point-fix") {
    page = <PilotageThreePointFix onNavigate={navigate} />;
  } else if (pathname === "/play/soundings") {
    page = <Soundings onNavigate={navigate} />;
  } else if (pathname === "/play/soundings/multi") {
    page = <SoundingsMulti onNavigate={navigate} />;
  } else if (pathname === "/play/day-shapes") {
    page = <DayShapes onNavigate={navigate} />;
  } else if (pathname === "/play/day-shapes/flashcards") {
    page = <DayShapesFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/buoyage") {
    page = <Buoyage onNavigate={navigate} />;
  } else if (pathname === "/play/buoyage/flashcards") {
    page = <BuoyageFlashcards onNavigate={navigate} />;
  } else if (pathname === "/play/irpcs") {
    page = <Irpcs onNavigate={navigate} />;
  } else if (pathname === "/play/irpcs/scenarios") {
    page = <IrpcsScenarios onNavigate={navigate} />;
  } else if (pathname === "/play/exam") {
    page = <Exam onNavigate={navigate} />;
  } else if (pathname === "/play/reference") {
    page = <Reference onNavigate={navigate} />;
  } else if (pathname === "/play/listen") {
    page = <BroadcastListen onNavigate={navigate} />;
  } else if (pathname === "/play/multiplayer") {
    page = <Multiplayer onNavigate={navigate} />;
  } else {
    page = <Home auth={auth} onNavigate={navigate} />;
  }

  return (
    <Shell signedIn={!!session} pathname={pathname} onNavigate={navigate}>
      {page}
    </Shell>
  );
}

interface ShellProps {
  children: React.ReactNode;
  signedIn?: boolean;
  pathname: string;
  onNavigate?: (to: string) => void;
}

interface NavEntry {
  to: string;
  label: string;
  /// Prefix-match: any pathname starting with `match` (or equal to
  /// it) lights up this nav button. Lets us highlight "Games" for
  /// every /play/* page except the dedicated ones below.
  match: string;
}

const NAV_ENTRIES: readonly NavEntry[] = [
  { to: "/play", label: "Games", match: "/play" },
  { to: "/drills", label: "Drills", match: "/drills" },
  { to: "/play/multiplayer", label: "Multi-player", match: "/play/multiplayer" },
  { to: "/play/reference", label: "Reference", match: "/play/reference" },
  { to: "/me", label: "Account", match: "/me" },
];

function isActive(pathname: string, entry: NavEntry): boolean {
  // Dedicated routes (Reference, Multi-player, Account) take precedence
  // over the generic /play prefix so "Games" doesn't also light up when
  // you're inside Reference or Multi-player.
  if (entry.to === "/play") {
    if (pathname.startsWith("/play/reference")) return false;
    if (pathname.startsWith("/play/multiplayer")) return false;
    return pathname.startsWith("/play");
  }
  return pathname === entry.match || pathname.startsWith(`${entry.match}/`);
}

function Shell({ children, signedIn, pathname, onNavigate }: ShellProps) {
  return (
    <div className="container">
      <header className="header">
        <a
          href="/"
          className="brand"
          onClick={(e) => {
            e.preventDefault();
            onNavigate?.("/");
          }}
        >
          <Logo size={32} />
          <h1>nav-games</h1>
        </a>
        <nav aria-label="Primary">
          {signedIn &&
            NAV_ENTRIES.map((entry) => (
              <a
                key={entry.to}
                href={entry.to}
                className={`nav-btn${isActive(pathname, entry) ? " active" : ""}`}
                aria-current={isActive(pathname, entry) ? "page" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate?.(entry.to);
                }}
              >
                {entry.label}
              </a>
            ))}
        </nav>
      </header>
      {children}
      <footer className="footer">
        <span className="muted small">
          Part of{" "}
          <a
            href="https://goastern.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            GoAstern
          </a>{" "}
          — seamanship tools for sailors.
        </span>
        <nav className="footer-links" aria-label="GoAstern">
          <a href="https://goastern.com" target="_blank" rel="noopener noreferrer">
            goastern.com
          </a>
          <a
            href="https://goastern.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>
        </nav>
      </footer>
    </div>
  );
}
